import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ErrorCode } from '@shared/models/error-code.enum';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SetGenericApiErrorCode } from '../store/error/api-errors.action';
import { ApiErrorsState } from '../store/error/api-errors.reducer';

interface ErrorRule {
  status: number;
  exclusions?: ErrorRuleExclusion[];
}

interface ErrorRuleExclusion {
  url?: string;
  errorCodes?: ErrorCode[];
  nonFieldErrorCodes?: ErrorCode[];
}

@Injectable()
export class GenericApiErrorsInterceptorService implements HttpInterceptor {
  /*
   * Show the generic error message if one of these rules matches
   * A rule matches if the status matches the errors' status, and none of the exclusion rules match the error
   */
  private readonly genericErrorRules: ErrorRule[] = [
    { status: 500 },
    { status: 503 },
    {
      status: 400,
      exclusions: [
        { url: '/cards' },
        { url: 'api/search/dining' },
        { url: 'v1/external_or_client_verified_eligibility' },
        { url: 'self_register', errorCodes: [ErrorCode.EmailNotUnique] },
        { url: 'member_events_bookings', nonFieldErrorCodes: [ErrorCode.OnDemandBookingExists] },
        {
          nonFieldErrorCodes: [
            ErrorCode.RedemptionBenefitAlreadyBeenRedeemed,
            ErrorCode.RedemptionBenefitFullyBooked,
            ErrorCode.BenefitsApiResponseError,
          ],
        },
      ],
    },
  ];

  constructor(private store: Store<ApiErrorsState>) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.genericErrorRules.some((rule) => this.isMatchingRule(rule, error))) {
          this.store.dispatch(new SetGenericApiErrorCode(error.status));
        }

        return throwError(error);
      })
    );
  }

  private isMatchingRule(rule: ErrorRule, error: HttpErrorResponse): boolean {
    if (error.status !== rule.status) {
      return false;
    }

    return rule.exclusions?.every((ex) => !this.isMatchingExclusion(ex, error)) ?? true;
  }

  private isMatchingExclusion(exclusions: ErrorRuleExclusion, error: HttpErrorResponse): boolean {
    if (exclusions.url && !error.url.includes(exclusions.url)) {
      return false;
    }

    if (exclusions.errorCodes && !exclusions.errorCodes.includes(error.error?.[0]?.errorCode)) {
      return false;
    }

    if (
      exclusions.nonFieldErrorCodes &&
      !exclusions.nonFieldErrorCodes.includes(error.error?.non_field_errors?.[0]?.code)
    ) {
      return false;
    }

    return true;
  }
}
